import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Badge, Table, Button, Modal, Form,
  Spinner, Alert, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faCheckCircle, faExclamationTriangle, faClock,
  faPlus, faEdit, faTrash, faFilter, faSyncAlt, faFileInvoiceDollar,
  faBell, faBuilding, faScroll,
} from '@fortawesome/free-solid-svg-icons';
import {
  getScadenzario, aggiornaScadenzaFiscale, eliminaScadenzaFiscale,
  creaScadenzaFiscale,
} from '../../services/api';

const ANNO_CORRENTE = new Date().getFullYear();
const fmt = (v) => v != null ? `€ ${parseFloat(v).toFixed(2)}` : '—';
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT') : '—';

// ─── Configurazione semaforo ────────────────────────────────────────────────

const URGENZA_CONFIG = {
  scaduta:    { bg: 'danger',   icon: faExclamationTriangle, label: 'Scaduta' },
  imminente:  { bg: 'warning',  icon: faBell,                label: '≤ 7 giorni' },
  futura:     { bg: 'primary',  icon: faClock,               label: 'Futura' },
  completata: { bg: 'success',  icon: faCheckCircle,         label: 'Completata' },
};

const TIPO_CONFIG = {
  iva:                  { icon: faFileInvoiceDollar, color: '#dc3545', label: 'IVA' },
  lipe:                 { icon: faScroll,            color: '#6610f2', label: 'LIPE' },
  dichiarazione_iva:    { icon: faScroll,            color: '#6610f2', label: 'Dich. IVA' },
  dichiarazione_redditi:{ icon: faScroll,            color: '#0d6efd', label: 'Dich. Redditi' },
  f24_generico:         { icon: faFileInvoiceDollar, color: '#fd7e14', label: 'F24' },
  fornitore:            { icon: faBuilding,          color: '#198754', label: 'Fornitore' },
  altro:                { icon: faCalendarAlt,        color: '#6c757d', label: 'Altro' },
};

// ─── Modal segna eseguita / modifica ───────────────────────────────────────

function ModificaModal({ item, onClose, onSave }) {
  const isFiscale = !!item.scadenza_fiscale_id;
  const [stato, setStato] = useState(item.stato || 'aperta');
  const [dataEsecuzione, setDataEsecuzione] = useState(
    item.data_esecuzione || new Date().toISOString().split('T')[0]
  );
  const [riferimento, setRiferimento] = useState(item.riferimento || '');
  const [importo, setImporto] = useState(item.importo ?? '');
  const [note, setNote] = useState(item.note || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await aggiornaScadenzaFiscale(item.scadenza_fiscale_id, {
        stato,
        data_esecuzione: stato === 'eseguita' ? dataEsecuzione : null,
        riferimento: riferimento || null,
        importo: importo !== '' ? parseFloat(importo) : null,
        note: note || null,
      });
      toast.success('Scadenza aggiornata');
      onSave();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (!isFiscale) {
    // Le scadenze IVA e fornitori si gestiscono dalle rispettive pagine
    return (
      <Modal show onHide={onClose}>
        <Modal.Header closeButton>
          <Modal.Title>{item.descrizione}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-0">
            {item.tipo === 'iva'
              ? 'Gestisci questo versamento dalla pagina Liquidazione IVA.'
              : 'Gestisci questo pagamento dalla pagina Fatture Passive.'}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>Chiudi</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">{item.descrizione}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2">
          <Col md={6}>
            <Form.Label className="small">Stato</Form.Label>
            <Form.Select size="sm" value={stato} onChange={e => setStato(e.target.value)}>
              <option value="aperta">Aperta</option>
              <option value="eseguita">Eseguita</option>
              <option value="non_applicabile">Non applicabile</option>
            </Form.Select>
          </Col>
          {stato === 'eseguita' && (
            <Col md={6}>
              <Form.Label className="small">Data esecuzione</Form.Label>
              <Form.Control size="sm" type="date" value={dataEsecuzione}
                onChange={e => setDataEsecuzione(e.target.value)} />
            </Col>
          )}
          <Col md={6}>
            <Form.Label className="small">Riferimento / protocollo</Form.Label>
            <Form.Control size="sm" type="text" value={riferimento}
              onChange={e => setRiferimento(e.target.value)}
              placeholder="es. telematico 2025-0001" />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Importo (€)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" value={importo}
              onChange={e => setImporto(e.target.value)} />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Note</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={note}
              onChange={e => setNote(e.target.value)} />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Chiudi</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : null}Salva
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Modal nuova scadenza manuale ───────────────────────────────────────────

function NuovaScadenzaModal({ anno, onClose, onSave }) {
  const [tipo, setTipo] = useState('altro');
  const [descrizione, setDescrizione] = useState('');
  const [dataScadenza, setDataScadenza] = useState('');
  const [importo, setImporto] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!descrizione || !dataScadenza) {
      toast.error('Descrizione e data sono obbligatorie');
      return;
    }
    setSaving(true);
    try {
      await creaScadenzaFiscale({
        anno,
        tipo,
        descrizione,
        data_scadenza: dataScadenza,
        importo: importo !== '' ? parseFloat(importo) : null,
      });
      toast.success('Scadenza aggiunta');
      onSave();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Nuova scadenza manuale
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2">
          <Col md={6}>
            <Form.Label className="small">Tipo</Form.Label>
            <Form.Select size="sm" value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="lipe">LIPE</option>
              <option value="dichiarazione_iva">Dichiarazione IVA</option>
              <option value="dichiarazione_redditi">Dichiarazione Redditi</option>
              <option value="f24_generico">F24 generico</option>
              <option value="altro">Altro</option>
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="small">Data scadenza</Form.Label>
            <Form.Control size="sm" type="date" value={dataScadenza}
              onChange={e => setDataScadenza(e.target.value)} />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Descrizione *</Form.Label>
            <Form.Control size="sm" type="text" value={descrizione}
              onChange={e => setDescrizione(e.target.value)}
              placeholder="es. Invio LIPE Q2 2025" />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Importo (€)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" value={importo}
              onChange={e => setImporto(e.target.value)} />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : null}Aggiungi
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Riga tabella ───────────────────────────────────────────────────────────

function RigaScadenza({ item, onModifica, onElimina }) {
  const oggi = new Date();
  const dataS = new Date(item.data_scadenza + 'T00:00:00');
  const deltaGg = Math.round((dataS - oggi) / 86400000);
  const urg = URGENZA_CONFIG[item.urgenza] || URGENZA_CONFIG.futura;
  const tipoConf = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.altro;

  return (
    <tr style={{ opacity: item.urgenza === 'completata' ? 0.6 : 1 }}>
      <td className="text-center">
        <FontAwesomeIcon icon={urg.icon} className={`text-${urg.bg}`} title={urg.label} />
      </td>
      <td>
        <span className="fw-bold" style={{ color: tipoConf.color }}>
          <FontAwesomeIcon icon={tipoConf.icon} className="me-1" />
          {tipoConf.label}
        </span>
      </td>
      <td>{item.descrizione}</td>
      <td className="small fw-bold">
        {fmtDate(item.data_scadenza)}
        {item.urgenza !== 'completata' && (
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
            {deltaGg < 0 ? `${Math.abs(deltaGg)}gg fa` : deltaGg === 0 ? 'Oggi' : `tra ${deltaGg}gg`}
          </div>
        )}
      </td>
      <td className="text-end small">{fmt(item.importo)}</td>
      <td>
        <Badge bg={item.urgenza === 'completata' ? 'success' : item.urgenza === 'scaduta' ? 'danger' : 'secondary'}>
          {item.stato_display}
        </Badge>
      </td>
      <td className="small text-muted">{item.riferimento || '—'}</td>
      <td>
        <Button size="sm" variant="outline-secondary" className="me-1"
          onClick={() => onModifica(item)}
          title="Modifica / segna eseguita">
          <FontAwesomeIcon icon={faEdit} />
        </Button>
        {item.scadenza_fiscale_id && (
          <Button size="sm" variant="outline-danger"
            onClick={() => onElimina(item)}
            title="Elimina">
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        )}
      </td>
    </tr>
  );
}

// ─── Componente principale ──────────────────────────────────────────────────

export default function Scadenzario() {
  const [anno, setAnno] = useState(ANNO_CORRENTE);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNuova, setShowNuova] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getScadenzario(anno);
      setItems(res.data);
    } catch {
      toast.error('Errore nel caricamento scadenzario');
    } finally {
      setLoading(false);
    }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const handleElimina = async (item) => {
    if (!item.scadenza_fiscale_id) return;
    if (!window.confirm(`Eliminare "${item.descrizione}"?`)) return;
    try {
      await eliminaScadenzaFiscale(item.scadenza_fiscale_id);
      toast.success('Scadenza eliminata');
      load();
    } catch {
      toast.error('Errore nella eliminazione');
    }
  };

  // Filtraggio
  const itemiFiltrati = items.filter(i => {
    if (filtroTipo && i.tipo !== filtroTipo) return false;
    if (filtroStato === 'aperte' && i.urgenza === 'completata') return false;
    if (filtroStato === 'scadute' && i.urgenza !== 'scaduta') return false;
    if (filtroStato === 'imminenti' && i.urgenza !== 'imminente') return false;
    return true;
  });

  // KPI
  const scadute    = items.filter(i => i.urgenza === 'scaduta').length;
  const imminenti  = items.filter(i => i.urgenza === 'imminente').length;
  const completate = items.filter(i => i.urgenza === 'completata').length;
  const totAperte  = items.filter(i => i.urgenza !== 'completata').length;

  return (
    <div className="container-fluid py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
            Scadenzario Fiscale
          </h4>
          <small className="text-muted">IVA trimestrale · LIPE · Dichiarazioni · Pagamenti fornitori</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Form.Select value={anno} onChange={e => setAnno(+e.target.value)} style={{ width: 100 }}>
            {[ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE + 1].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" size="sm" onClick={load} title="Aggiorna">
            <FontAwesomeIcon icon={faSyncAlt} />
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowNuova(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />Aggiungi
          </Button>
        </Col>
      </Row>

      {/* KPI semaforo */}
      <Row className="g-2 mb-3">
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-danger bg-opacity-10 py-2">
            <div className="small text-danger fw-bold">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />Scadute
            </div>
            <div className="fs-4 fw-bold text-danger">{scadute}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-warning bg-opacity-10 py-2">
            <div className="small text-warning fw-bold">
              <FontAwesomeIcon icon={faBell} className="me-1" />Imminenti
            </div>
            <div className="fs-4 fw-bold text-warning">{imminenti}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-primary bg-opacity-10 py-2">
            <div className="small text-primary fw-bold">
              <FontAwesomeIcon icon={faClock} className="me-1" />Aperte totali
            </div>
            <div className="fs-4 fw-bold text-primary">{totAperte}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-success bg-opacity-10 py-2">
            <div className="small text-success fw-bold">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />Completate
            </div>
            <div className="fs-4 fw-bold text-success">{completate}</div>
          </Card>
        </Col>
      </Row>

      {/* Filtri */}
      <Row className="g-2 mb-3 align-items-center">
        <Col xs="auto">
          <FontAwesomeIcon icon={faFilter} className="text-muted me-1" />
          <small className="text-muted">Filtra:</small>
        </Col>
        <Col xs="auto">
          <Form.Select size="sm" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ width: 160 }}>
            <option value="">Tutti i tipi</option>
            <option value="iva">IVA</option>
            <option value="lipe">LIPE</option>
            <option value="dichiarazione_iva">Dich. IVA</option>
            <option value="dichiarazione_redditi">Dich. Redditi</option>
            <option value="f24_generico">F24</option>
            <option value="fornitore">Fornitori</option>
            <option value="altro">Altro</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Form.Select size="sm" value={filtroStato} onChange={e => setFiltroStato(e.target.value)} style={{ width: 140 }}>
            <option value="">Tutti gli stati</option>
            <option value="scadute">Solo scadute</option>
            <option value="imminenti">Solo imminenti</option>
            <option value="aperte">Solo aperte</option>
          </Form.Select>
        </Col>
        {(filtroTipo || filtroStato) && (
          <Col xs="auto">
            <Button size="sm" variant="outline-secondary"
              onClick={() => { setFiltroTipo(''); setFiltroStato(''); }}>
              Reimposta
            </Button>
          </Col>
        )}
        <Col xs="auto" className="ms-auto">
          <small className="text-muted">{itemiFiltrati.length} di {items.length} scadenze</small>
        </Col>
      </Row>

      {loading && <div className="text-center py-5"><Spinner /></div>}

      {!loading && itemiFiltrati.length === 0 && (
        <Alert variant="info">
          Nessuna scadenza trovata per i filtri selezionati.
        </Alert>
      )}

      {!loading && itemiFiltrati.length > 0 && (
        <Table responsive bordered hover size="sm">
          <thead className="table-dark">
            <tr>
              <th style={{ width: 36 }}></th>
              <th style={{ width: 120 }}>Tipo</th>
              <th>Descrizione</th>
              <th style={{ width: 110 }}>Scadenza</th>
              <th className="text-end" style={{ width: 110 }}>Importo</th>
              <th style={{ width: 110 }}>Stato</th>
              <th style={{ width: 130 }}>Riferimento</th>
              <th style={{ width: 80 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {itemiFiltrati.map(item => (
              <RigaScadenza
                key={item.id}
                item={item}
                onModifica={setSelected}
                onElimina={handleElimina}
              />
            ))}
          </tbody>
        </Table>
      )}

      {selected && (
        <ModificaModal
          item={selected}
          onClose={() => setSelected(null)}
          onSave={() => { setSelected(null); load(); }}
        />
      )}

      {showNuova && (
        <NuovaScadenzaModal
          anno={anno}
          onClose={() => setShowNuova(false)}
          onSave={() => { setShowNuova(false); load(); }}
        />
      )}
    </div>
  );
}
